import { ApiRequestError } from "@/utils/fetch";

const DECLINE_COPY: Record<string, string> = {
  insufficient_funds: "Your card was declined for insufficient funds.",
  expired_card: "Your card has expired. Please use a different card.",
  incorrect_cvc: "The card's security code is incorrect.",
  card_declined: "Your card was declined. Please try a different card.",
};

const GENERIC_DECLINE_COPY =
  "Your card was declined. Please try a different card.";
const GENERIC_SAVE_COPY = "We couldn't save your card. Please try again.";
const PARTNER_UNAVAILABLE_COPY =
  "A billing partner is temporarily unavailable. Try again shortly.";

function declineCopy(code: string | undefined): string | undefined {
  return code && Object.hasOwn(DECLINE_COPY, code)
    ? DECLINE_COPY[code]
    : undefined;
}

/**
 * Maps Stripe.js setup-time failures to stable card-entry copy. Decline codes
 * take precedence over the broad error code; provider prose is never exposed.
 */
export function stripeSetupErrorMessage(error: {
  code?: string;
  decline_code?: string;
  type?: string;
}): string {
  const knownCopy = declineCopy(error.decline_code) ?? declineCopy(error.code);
  if (knownCopy) return knownCopy;
  if (error.code === "card_declined") {
    return GENERIC_DECLINE_COPY;
  }
  return GENERIC_SAVE_COPY;
}

/** Returns whether a billing error carries the exact `402 payment_method_required` contract. */
export function isPaymentMethodRequiredError(err: unknown): boolean {
  if (!(err instanceof ApiRequestError) || err.status !== 402) return false;
  const body = err.body as { error?: string } | null;
  return body?.error === "payment_method_required";
}

/**
 * Maps a billing-endpoint failure to user-facing copy. Understands the 402
 * contracts (`payment_declined` with an optional Stripe code, and
 * `payment_method_required`) plus the retryable partner-outage contract;
 * anything else gets generic card-save copy.
 */
export function paymentErrorMessage(err: unknown): string {
  if (err instanceof ApiRequestError) {
    const body = err.body as { error?: string; code?: string } | null;

    if (err.status === 402 && body?.error === "payment_declined") {
      if (body.code === "card_save_failed") return GENERIC_SAVE_COPY;
      return declineCopy(body.code) ?? GENERIC_DECLINE_COPY;
    }
    if (isPaymentMethodRequiredError(err)) {
      return "A valid payment method is required to activate your account.";
    }
    if (err.status === 503 && body?.error === "billing_provider_unavailable") {
      return PARTNER_UNAVAILABLE_COPY;
    }
  }

  return GENERIC_SAVE_COPY;
}
